import Cookies from 'universal-cookie';

const PUBLIC_VAPID_KEY = process.env.NEXT_PUBLIC_WEBPUSH_PUBLIC_VAPID_KEY;
const DAYS_UNTIL_REFRESH_TOKEN = 20;

const cookies = new Cookies();

const checkIfPushNotificationSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

const registerServiceWorker = async () => {
  try {
    const registration = await navigator.serviceWorker.register('./serviceWorker.js');
    return registration;
  } catch (error) {
    console.log(`[Service Worker] Failed to register service worker due to ${error}`);
    return null;
  }
};

const unregisterServiceWorker = async () => {
  let registeredServiceWorkers;
  try {
    registeredServiceWorkers = await navigator.serviceWorker.getRegistrations();
  } catch (error) {
    console.log(`[Service Worker] Failed to unregister service worker due to ${error}`);
    console.log(`[Service Worker] There's no resgistered service worker`);
  }
  registeredServiceWorkers?.forEach(registration => registration.unregister());
};

const checkIfPushSubscriptionValid = () => {
  const savedPushSubscription = cookies.get('webPushToken');
  return !!savedPushSubscription;
};

const saveSubscriptionToCookies = (subscription: PushSubscription) => {
  const stringifiedPushSubscription = JSON.stringify(subscription);
  cookies.set('webPushToken', stringifiedPushSubscription, {
    path: '/',
    expires: new Date(Date.now() + DAYS_UNTIL_REFRESH_TOKEN * 24 * 60 * 60 * 1000),
  });
};

const setOldWebPushToken = (subscription: PushSubscription) => {
  const stringifiedPushSubscription = JSON.stringify(subscription);
  cookies.set('oldWebPushToken', stringifiedPushSubscription, {
    path: '/',
    expires: new Date('9999-12-31'),
  });
};

const askPermission = async () => {
  return await Notification.requestPermission();
};

const base64UrlToUint8Array = (base64UrlData: string) => {
  const padding = '='.repeat((4 - (base64UrlData.length % 4)) % 4);
  const base64 = (base64UrlData + padding).replace(/\-/g, '+').replace(/_/g, '/'); // eslint-disable-line no-useless-escape
  const rawData = atob(base64);
  const buffer = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    buffer[i] = rawData.charCodeAt(i);
  }
  return buffer;
};

const createNotificationSubscription = async () => {
  const serviceWorker = await navigator.serviceWorker.ready;
  try {
    const pushSubscription = await serviceWorker.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(PUBLIC_VAPID_KEY as string),
    });
    saveSubscriptionToCookies(pushSubscription);
    return pushSubscription;
  } catch (error) {
    console.log(`[Service Worker] Failed to create notification subscription due to ${error}`);
    return;
  }
};

const getUserSubscription = () => {
  const savedPushSubscription = cookies.get('webPushToken');
  return savedPushSubscription;
};

const buildWebPushSubscriptionToken = (subscription: PushSubscription) => {
  const device = navigator.userAgent;
  const stringifiedSubscription = JSON.stringify(subscription);
  const parsedSubscription = JSON.parse(stringifiedSubscription);
  const oldKeysP256dh = cookies.get('oldWebPushToken')?.keys.p256dh || null;
  return {
    endpoint: parsedSubscription.endpoint,
    keysP256dh: parsedSubscription.keys.p256dh,
    keysAuth: parsedSubscription.keys.auth,
    oldKeysP256dh,
    device,
  };
};

const destroyWebPushToken = () => {
  cookies.remove('webPushToken');
  cookies.remove('oldWebPushToken');
};

export {
  checkIfPushNotificationSupported,
  registerServiceWorker,
  unregisterServiceWorker,
  askPermission,
  createNotificationSubscription,
  getUserSubscription,
  buildWebPushSubscriptionToken,
  checkIfPushSubscriptionValid,
  setOldWebPushToken,
  destroyWebPushToken,
};
